import { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { DocumentTextIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { Modal, TemplateInput, TemplateTextarea, Logo } from "@components";
import EmailFormattingToolbar from "@components/paymentTemplates/EmailFormattingToolbar";
import { templateTokens } from "@utils";
import { useMerchant } from "@auth";
import { useAllUsers } from "@api/usersController";
import AiSpinner from "./AiSpinner";
import {
  MAX_EMAIL_COPIES,
  MAX_EMAIL_RECIPIENTS,
  editableEmailStep,
  emailAddressList,
  emailRecipients,
} from "./actionAmendments";
import RecipientListRow from "./RecipientListRow";

// Document emails don't resolve template tokens (unlike payment requests), so offer none here — the
// TemplateInput/TemplateTextarea token affordance is suppressed when nothing is available.
const NO_TOKENS = templateTokens.map((t) => t.value);

// Browsers render these inline as an <img> thumbnail; TIFF falls back to a document icon.
const isImage = (name) => /\.(jpe?g|png|gif|webp|bmp)$/i.test(name || "");
// PDFs preview via an <iframe> (the browser renders the first page).
const isPdf = (name) => /\.pdf$/i.test(name || "");

// Show the invoice/sales-order number rather than the opaque ERP file name. A POD rides the same number as
// its invoice, so mark it apart (the invoice an email also carries keeps the bare number).
const attachmentLabel = (a) => {
  const base = a.reference || a.fileName || "";
  return /proofofdelivery/i.test(a.documentType || "") ? `${base} (POD)` : base;
};

// Preview + edit for a customer email before it goes out (OP-455) — a plain email, or one carrying
// documents. A minimal take on the payment-request preview: the same edit-in-place branded email (reusing
// TemplateInput / TemplateTextarea), stripped to just the subject and body — this email has no amount or
// pay button. Below it, any materialized attachments (already produced by customers-service) with
// per-document toggles. On Send it hands the edited values up as email.send amendments.
// `unavailable` lists (by label) documents that were asked for but couldn't be generated; when given
// with `onRetryUnavailable`, the modal offers to try them again.
export default function SendEmailPreviewModal({
  isOpen,
  suggestion,
  customer,
  onSend,
  onClose,
  sending = false,
  unavailable = [],
  onRetryUnavailable,
}) {
  const { merchant } = useMerchant();
  const { data: users = [] } = useAllUsers();
  const titleEditorRef = useRef(null);
  const bodyEditorRef = useRef(null);
  const { control, getValues, reset } = useForm({
    defaultValues: { emailSubject: "", emailTitle: "", emailMainText: "" },
  });

  const step = editableEmailStep(suggestion?.params?.steps);
  const payload = step?.payload ?? {};
  const config = suggestion?.params?.requestConfig ?? {};
  // The step's recipients (the model's or the customer record's), falling back to the requestConfig
  // email the backend stamped. To, Cc and Bcc are each edited as a list of pills.
  const seededRecipients = useMemo(() => {
    const fromStep = emailRecipients(payload);
    return fromStep.length > 0 ? fromStep : config.email ? [config.email] : [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggestion]);
  const [recipients, setRecipients] = useState([]);
  const [cc, setCc] = useState([]);
  const [bcc, setBcc] = useState([]);

  // What each row's dropdown offers: the account's addresses first (the record's email, plus the
  // suggestion's own recipient when it differs), then the merchant's staff by name. The customer record
  // carries one email today; a secondary address slots into the same group when the data exists.
  const suggestions = useMemo(() => {
    const account = [customer?.email, config.email]
      .filter(Boolean)
      .filter((email, i, all) => all.findIndex((e) => e.toLowerCase() === email.toLowerCase()) === i)
      .map((email) => ({ email, name: customer?.name }));
    const staff = users
      .filter((user) => user.email)
      .map((user) => ({ email: user.email, name: user.name }))
      .sort((a, b) => (a.name ?? a.email).localeCompare(b.name ?? b.email));
    return [
      { label: "Account", addresses: account },
      { label: "Staff", addresses: staff },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer?.email, customer?.name, config.email, users]);
  // Every address already on some line, so no row offers or accepts it again.
  const onEmail = new Set([...recipients, ...cc, ...bcc]);
  // Which header rows are mid-edit — Send waits until every one is committed.
  const [editingRows, setEditingRows] = useState(() => new Set());
  const rowEditing = (row) => (isEditing) =>
    setEditingRows((prev) => {
      const next = new Set(prev);
      isEditing ? next.add(row) : next.delete(row);
      return next;
    });
  const merchantName = merchant?.name ?? "Your company";

  // requestConfig.documents and the step's attachment_locations are built in the same order upstream,
  // so they zip by index — each display row maps to one durable S3 key.
  const attachments = useMemo(() => {
    const docs = Array.isArray(config.documents) ? config.documents : [];
    const keys = Array.isArray(payload.attachment_locations)
      ? payload.attachment_locations
      : [];
    return docs.map((d, i) => ({
      reference: d.reference,
      documentType: d.documentType,
      previewUrl: d.previewUrl,
      fileName: d.fileName,
      s3Key: keys[i],
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggestion]);

  // Keyed by S3 key, not index, so a suggestion that gains documents mid-session keeps the user's toggles.
  const [included, setIncluded] = useState(() => new Set());
  const lastSeedRef = useRef(null);

  useEffect(() => {
    if (!isOpen) {
      lastSeedRef.current = null;
      return;
    }
    // Title and subject start identical (whatever the model composed) but are edited independently — the
    // title is the in-email heading, the subject is the email's subject line. Fall back to the subject
    // when the step carries no distinct title.
    const text = {
      emailSubject: payload.subject ?? "",
      emailTitle: payload.title ?? payload.subject ?? "",
      emailMainText: payload.body ?? "",
    };
    const keys = attachments.map((a) => a.s3Key).filter(Boolean);
    const previous = lastSeedRef.current;
    lastSeedRef.current = { text, keys };

    if (!previous) {
      reset(text);
      setIncluded(new Set(keys));
      setRecipients(seededRecipients);
      setCc(emailAddressList(payload, "cc_emails"));
      setBcc(emailAddressList(payload, "bcc_emails"));
      setEditingRows(new Set());
      return;
    }

    // The suggestion changed while open (e.g. a retried document arrived): take the new copy only for
    // fields the user hasn't touched, and switch newly arrived attachments on.
    const current = getValues();
    reset(
      Object.fromEntries(
        Object.entries(text).map(([field, value]) => [
          field,
          current[field] === previous.text[field] ? value : current[field],
        ]),
      ),
    );
    setIncluded((prev) => {
      const next = new Set([...prev].filter((key) => keys.includes(key)));
      keys.filter((key) => !previous.keys.includes(key)).forEach((key) => next.add(key));
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, suggestion]);

  const toggle = (key) =>
    setIncluded((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  const keptLocations = attachments.map((a) => a.s3Key).filter((key) => key && included.has(key));

  const [retrying, setRetrying] = useState(false);
  const retryUnavailable = async () => {
    setRetrying(true);
    try {
      await onRetryUnavailable();
    } finally {
      setRetrying(false);
    }
  };

  // A suggestion that arrived with no documents is a plain email and sends as is. One that arrived
  // WITH documents must keep at least one: its body describes the attachments, so sending none
  // would lie.
  const hasAttachments = attachments.length > 0;

  const sendDisabled =
    sending ||
    editingRows.size > 0 ||
    recipients.length === 0 ||
    (hasAttachments && keptLocations.length === 0);

  const handleSend = () => {
    if (sendDisabled) return;
    const values = getValues();
    onSend({
      subject: (values.emailSubject ?? "").trim(),
      title: (values.emailTitle ?? "").trim(),
      body: values.emailMainText ?? "",
      attachmentLocations: keptLocations,
      recipients,
      cc,
      bcc,
    });
  };

  return (
    <Modal
      title="Edit email"
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      fullHeight
      maxHeight="780px"
      elevated
      bodyClassName="flex min-h-0 flex-col overflow-hidden bg-white"
    >
      <div className="flex shrink-0 items-center border-b border-zinc-200 bg-white px-4 py-2">
        <EmailFormattingToolbar editorRefs={[titleEditorRef, bodyEditorRef]} />
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="flex w-full flex-col items-center gap-6 px-6 pb-8 pt-6">
          {/* Edit-in-place branded email — subject + body only (no pay button, unlike a payment request). */}
          <div
            className="flex w-full flex-col items-stretch rounded-3xl bg-white pt-6"
            style={{ boxShadow: "4px 4px 16px 0px rgba(0,0,0,0.16)" }}
          >
            <div className="border-b border-zinc-200 px-6 py-4">
              <div className="space-y-1 px-2 pb-2 text-xs">
                <div className="flex items-center px-2 py-1">
                  <span className="w-20 shrink-0 text-zinc-400">From:</span>
                  <span className="text-zinc-400">{merchantName}</span>
                </div>
                <RecipientListRow
                  label="To:"
                  addresses={recipients}
                  onChange={setRecipients}
                  max={MAX_EMAIL_RECIPIENTS}
                  minimum={1}
                  emptyText="the customer"
                  disabled={sending}
                  suggestions={suggestions}
                  excluded={onEmail}
                  onEditingChange={rowEditing("to")}
                />
                <RecipientListRow
                  label="Cc:"
                  addresses={cc}
                  onChange={setCc}
                  max={MAX_EMAIL_COPIES}
                  disabled={sending}
                  suggestions={suggestions}
                  excluded={onEmail}
                  onEditingChange={rowEditing("cc")}
                />
                <RecipientListRow
                  label="Bcc:"
                  addresses={bcc}
                  onChange={setBcc}
                  max={MAX_EMAIL_COPIES}
                  disabled={sending}
                  suggestions={suggestions}
                  excluded={onEmail}
                  onEditingChange={rowEditing("bcc")}
                />
                <div className="flex items-center px-2 py-1">
                  <span className="w-20 shrink-0 text-zinc-400">Subject:</span>
                  <div className="min-w-0 flex-1 font-bold text-zinc-800">
                    <TemplateInput
                      name="emailSubject"
                      control={control}
                      displayName="Subject"
                      placeholder="e.g. Invoice 12345"
                      borderless
                      maxLength={150}
                      className="text-xs"
                      blacklistedTokens={NO_TOKENS}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="px-6">
              <div className="flex justify-center p-3 pt-7">
                <Logo
                  type="center"
                  brand={merchant?.organisationId}
                  height="96px"
                  width="200px"
                />
              </div>
              {/* The in-email heading — editable, and independent of the subject line above. */}
              <div className="mt-4 text-center text-4xl font-bold">
                <TemplateInput
                  name="emailTitle"
                  control={control}
                  innerRef={titleEditorRef}
                  displayName="Title"
                  placeholder="Email title"
                  borderless
                  wrap
                  rich
                  maxLength={150}
                  className="text-center text-2xl"
                  blacklistedTokens={NO_TOKENS}
                />
              </div>
              <div className="my-8 px-6">
                <TemplateTextarea
                  name="emailMainText"
                  control={control}
                  innerRef={bodyEditorRef}
                  displayName="Email body"
                  placeholder="e.g. Hi, please find your document attached."
                  borderless
                  rich
                  maxLength={2000}
                  className="text-xs"
                  blacklistedTokens={NO_TOKENS}
                />
              </div>
              <div className="my-6 flex items-end justify-center">
                <p className="mb-8 text-xs font-medium text-zinc-700">
                  Powered by <strong>Ontik</strong>
                </p>
              </div>
            </div>
          </div>

          {unavailable.length > 0 && (
            <UnavailableDocumentsNotice
              labels={unavailable}
              retrying={retrying}
              onRetry={onRetryUnavailable ? retryUnavailable : null}
            />
          )}

          {/* Attachments — the materialized documents; toggle any off before sending. A plain
              email has none, so the panel is left out rather than announcing an empty list. */}
          {hasAttachments && (
            <div className="w-full rounded-2xl border border-zinc-200 bg-white p-4">
              <div className="mb-3 text-xs font-medium text-zinc-500">
                Attachments ({keptLocations.length})
              </div>
              <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                {attachments.map((a, i) => (
                  <li
                    key={a.s3Key ?? i}
                    className={`relative overflow-hidden rounded-lg border ${
                      included.has(a.s3Key)
                        ? "border-zinc-200"
                        : "border-dashed border-zinc-300 opacity-50"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={included.has(a.s3Key)}
                      onChange={() => toggle(a.s3Key)}
                      aria-label={`Include ${attachmentLabel(a)}`}
                      className="absolute left-2 top-2 z-10 size-4 rounded border-zinc-300 bg-white accent-primary-600"
                    />
                    <a
                      href={a.previewUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Open in a new tab"
                      className="block"
                    >
                      <div className="relative flex h-24 items-center justify-center overflow-hidden bg-zinc-50">
                        <DocumentTextIcon
                          aria-hidden="true"
                          className="size-8 text-zinc-300"
                        />
                        {a.previewUrl && isImage(a.fileName) && (
                          <img
                            src={a.previewUrl}
                            alt=""
                            loading="lazy"
                            onError={(e) => {
                              e.currentTarget.style.display = "none";
                            }}
                            className="absolute inset-0 size-full object-cover"
                          />
                        )}
                        {a.previewUrl && isPdf(a.fileName) && (
                          // pointer-events-none so the click passes through to the wrapping link; the icon
                          // behind shows if the browser can't render the PDF inline.
                          <iframe
                            src={`${a.previewUrl}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
                            title={a.fileName || "PDF preview"}
                            loading="lazy"
                            tabIndex={-1}
                            className="pointer-events-none absolute inset-0 size-full border-0 bg-white"
                          />
                        )}
                      </div>
                      <div
                        className="truncate px-2 py-1.5 text-xs text-zinc-700"
                        title={attachmentLabel(a)}
                      >
                        {attachmentLabel(a)}
                      </div>
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      <div className="flex shrink-0 items-center justify-between gap-3 border-t border-zinc-200 bg-white px-5 py-3">
        <button
          type="button"
          onClick={onClose}
          className="rounded-lg px-3 py-2 text-sm font-semibold text-zinc-600 hover:text-zinc-800"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSend}
          disabled={sendDisabled}
          className="flex min-w-[5rem] items-center justify-center rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:bg-zinc-300"
        >
          {sending ? <AiSpinner size={18} /> : "Send"}
        </button>
      </div>
    </Modal>
  );
}

// A generic notice for documents that couldn't be generated — it reads the same for one or forty, with
// the specifics tucked behind a toggle so a long list doesn't push the email out of view.
function UnavailableDocumentsNotice({ labels, retrying, onRetry }) {
  const [expanded, setExpanded] = useState(false);
  const count = labels.length;
  return (
    <div className="w-full rounded-2xl border border-amber-200 bg-amber-50 p-4">
      <div className="flex items-start gap-3">
        <ExclamationTriangleIcon aria-hidden="true" className="mt-0.5 size-5 shrink-0 text-amber-600" />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-amber-900">
            {count === 1 ? "1 document" : `${count} documents`} couldn't be generated
          </div>
          <div className="mt-0.5 text-xs text-amber-800">
            {count === 1 ? "It won't" : "They won't"} be attached to this email.{" "}
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="font-medium underline underline-offset-2 hover:text-amber-950"
            >
              {expanded ? "Hide" : "Show which"}
            </button>
          </div>
          {expanded && (
            <ul className="mt-2 max-h-32 space-y-0.5 overflow-y-auto text-xs text-amber-900">
              {labels.map((label) => (
                <li key={label} className="truncate">
                  {label}
                </li>
              ))}
            </ul>
          )}
        </div>
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            disabled={retrying}
            className="flex min-w-[5.5rem] shrink-0 items-center justify-center rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-xs font-semibold text-amber-900 hover:bg-amber-100 disabled:opacity-60"
          >
            {retrying ? <AiSpinner size={14} /> : "Try again"}
          </button>
        )}
      </div>
    </div>
  );
}
